import { Button, Checkbox, DateInput, Input, Textarea } from '@orbind/ui'
import { useEffect, useRef, type CSSProperties } from 'react'
import { AGENDAS_CRIAVEIS, agendaInfo, type ErrosDoForm, type FormDeEvento } from './calendario.ts'

/**
 * Criação rápida no popover (design do calendário, 2026-10-31): título, quando,
 * dia inteiro, a agenda em fichas e a descrição. Convidados, local, link e
 * término em outro dia ficam em "Mais opções", que abre o formulário completo
 * com o que já foi digitado.
 */
export function CriacaoRapida({
  form,
  erros,
  tentou,
  googleConectado,
  salvando,
  onMudar,
  onSalvar,
  onMaisOpcoes,
  onFechar,
}: {
  form: FormDeEvento
  erros: ErrosDoForm
  /** Já tentou salvar: mostra todos os erros (antes, só o de horário). */
  tentou: boolean
  googleConectado: boolean
  salvando: boolean
  onMudar: (patch: Partial<FormDeEvento>) => void
  onSalvar: () => void
  onMaisOpcoes: () => void
  onFechar: () => void
}) {
  const titulo = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const t = setTimeout(() => titulo.current?.focus(), 30)
    return () => clearTimeout(t)
  }, [])

  const erro = tentou
    ? (erros.title ?? erros.date ?? erros.startTime ?? erros.endTime ?? null)
    : (erros.startTime ?? erros.endTime ?? null)
  const itens = agendaInfo('itens')

  return (
    <div className="cal-rapido">
      <div className="cal-rapido__topo">
        <span className="cal-rapido__rotulo">Novo evento</span>
        <button type="button" className="cal-round cal-round--md" aria-label="Fechar (Esc)" title="Fechar (Esc)" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>

      <div className="cal-rapido__recuo">
        <input
          ref={titulo}
          className="cal-rapido__titulo"
          value={form.title}
          placeholder="Adicionar título"
          aria-label="Título do evento"
          aria-invalid={tentou && Boolean(erros.title)}
          maxLength={300}
          onChange={(event) => onMudar({ title: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSalvar()
          }}
        />
      </div>

      <div className="cal-rapido__linha">
        <span className="cal-sheet__icone" aria-hidden>
          <i className="far fa-clock" />
        </span>
        <div className="cal-rapido__quando">
          <div className="cal-rapido__data">
            <DateInput value={form.date} clearable={false} onChange={(date) => onMudar({ date })} />
          </div>
          {form.diaInteiro ? null : (
            <>
              <Input
                className="cal-rapido__hora"
                value={form.startTime}
                placeholder="09:00"
                inputMode="numeric"
                maxLength={5}
                aria-label="Hora de início"
                onChange={(event) => onMudar({ startTime: event.target.value })}
              />
              <span className="cal-rapido__ate" aria-hidden>
                –
              </span>
              <Input
                className="cal-rapido__hora"
                value={form.endTime}
                placeholder="10:00"
                inputMode="numeric"
                maxLength={5}
                aria-label="Hora de término"
                onChange={(event) => onMudar({ endTime: event.target.value })}
              />
            </>
          )}
        </div>
      </div>

      <div className="cal-rapido__recuo cal-rapido__check">
        <Checkbox
          checked={form.diaInteiro}
          label="Dia inteiro"
          onChange={(event) => {
            const diaInteiro = event.target.checked
            onMudar(!diaInteiro && form.startTime === '' ? { diaInteiro, startTime: '09:00', endTime: '10:00' } : { diaInteiro })
          }}
        />
      </div>

      <div className="cal-rapido__linha">
        <span className="cal-sheet__icone" aria-hidden>
          <i className="far fa-calendar" />
        </span>
        <div className="cal-rapido__agendas" role="radiogroup" aria-label="Agenda">
          {AGENDAS_CRIAVEIS.map((a) => {
            const marcada = form.agenda === a.id
            return (
              <button
                key={a.id}
                type="button"
                role="radio"
                aria-checked={marcada}
                className={`cal-ficha${marcada ? ' cal-ficha--on' : ''}`}
                style={{ '--cal-ev-cor': a.color } as CSSProperties}
                onClick={() => onMudar({ agenda: a.id })}
              >
                <i className={a.icon} aria-hidden /> {a.short}
              </button>
            )
          })}
          <span
            className="cal-ficha cal-ficha--bloqueada"
            title="Criados pelo Orbind no início e na entrega de cada Item de Trabalho"
          >
            <i className={itens.icon} aria-hidden /> {itens.short} <i className="fas fa-lock" aria-hidden />
          </span>
        </div>
      </div>

      <div className="cal-rapido__linha">
        <span className="cal-sheet__icone" aria-hidden>
          <i className="fas fa-align-left" />
        </span>
        <Textarea
          rows={2}
          value={form.description}
          placeholder="Adicionar descrição"
          aria-label="Descrição"
          maxLength={10_000}
          onChange={(event) => onMudar({ description: event.target.value })}
        />
      </div>

      {googleConectado ? (
        <div className="cal-rapido__recuo cal-rapido__check">
          <Checkbox
            checked={form.googleSync}
            label="Enviar também para o meu Google Calendar"
            onChange={(event) => onMudar({ googleSync: event.target.checked })}
          />
        </div>
      ) : null}

      {erro ? (
        <p className="cal-rapido__erro" role="alert">
          {erro}
        </p>
      ) : null}

      <div className="cal-rapido__rodape">
        <Button variant="ghost" icon="fas fa-sliders-h" onClick={onMaisOpcoes}>
          Mais opções
        </Button>
        <span className="ob-grow" />
        <Button variant="ghost" onClick={onFechar}>
          Cancelar
        </Button>
        <Button variant="primary" disabled={form.title.trim() === '' || salvando} loading={salvando} onClick={onSalvar}>
          Salvar
        </Button>
      </div>
    </div>
  )
}
